import { Priority, Status } from '../types';

// Priority utilities
export const getPriorityColor = (p: Priority): string => {
    switch (p) {
        case 'P1': return 'bg-red-600 text-white animate-pulse shadow-md';
        case 'P2': return 'bg-orange-500 text-white shadow-sm';
        case 'P3': return 'bg-yellow-400 text-yellow-900 border border-yellow-500';
        case 'P4': return 'bg-gray-200 text-gray-700 border border-gray-300';
    }
};

export const getPriorityLabel = (p: Priority): string => {
    switch (p) {
        case 'P1': return 'Crítico';
        case 'P2': return 'Alto';
        case 'P3': return 'Medio';
        case 'P4': return 'Bajo';
    }
};

// Status utilities
export const getStatusColor = (s: Status): string => {
    switch (s) {
        case 'OPEN': return 'bg-purple-100 text-purple-800 border-2 border-purple-300 font-bold';
        case 'IN_PROGRESS': return 'bg-blue-600 text-white border-2 border-blue-700 shadow-sm';
        case 'IN_TRANSIT': return 'bg-indigo-600 text-white border-2 border-indigo-700 shadow-sm';
        case 'ON_SITE': return 'bg-orange-500 text-white border-2 border-orange-600 shadow-md animate-pulse';
        case 'WAITING_PARTS': return 'bg-pink-600 text-white border-2 border-pink-700';
        case 'WAITING_INFO': return 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400';
        case 'RESOLVED': return 'bg-green-600 text-white border-2 border-green-700 shadow-sm';
        case 'CLOSED': return 'bg-gray-800 text-white border-2 border-gray-900';
    }
};

export const getStatusLabel = (s: Status): string => {
    switch (s) {
        case 'OPEN': return '🆕 Nuevo';
        case 'IN_PROGRESS': return '👀 En Atención';
        case 'IN_TRANSIT': return '🚚 En Camino';
        case 'ON_SITE': return '🛠️ En Sitio / Reparando';
        case 'WAITING_PARTS': return '⚙️ Esp. Repuestos';
        case 'WAITING_INFO': return '🕓 Esp. Info';
        case 'RESOLVED': return '✅ Resuelto';
        case 'CLOSED': return '🔒 Cerrado';
    }
};

// Calculate priority based on impact and urgency
export const calculatePriority = (urgency: string, impact: string): Priority => {
    if (urgency === 'HIGH' && impact === 'SITE') return 'P1';
    if (urgency === 'HIGH' || impact === 'SITE') return 'P2';
    if (urgency === 'MEDIUM' || impact === 'SECTOR') return 'P3';
    return 'P4';
};

// Date formatting
export const formatDate = (date: Date): string => {
    return date.toLocaleDateString('es-CL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

export const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('es-CL', {
        hour: '2-digit',
        minute: '2-digit',
    });
};

export const formatDateTime = (date: Date): string => {
    return `${formatDate(date)} ${formatTime(date)}`;
};

export const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return formatDate(date);
};

// SLA utilities
export const getSLAStatus = (slaDue: Date): 'ok' | 'warning' | 'critical' | 'overdue' => {
    const now = new Date();
    const timeLeft = slaDue.getTime() - now.getTime();
    const hoursLeft = timeLeft / (1000 * 60 * 60);

    if (timeLeft < 0) return 'overdue';
    if (hoursLeft < 2) return 'critical';
    if (hoursLeft < 8) return 'warning';
    return 'ok';
};

export const getSLAColor = (status: 'ok' | 'warning' | 'critical' | 'overdue'): string => {
    switch (status) {
        case 'ok': return 'text-green-700 bg-green-100 border border-green-300';
        case 'warning': return 'text-yellow-800 bg-yellow-100 border border-yellow-300 font-bold';
        case 'critical': return 'text-white bg-orange-600 border border-orange-700 animate-pulse font-bold shadow-sm';
        case 'overdue': return 'text-white bg-red-700 border border-red-800 font-bold shadow-md';
    }
};

export const formatSLATime = (slaDue: Date): string => {
    const now = new Date();
    const timeLeft = slaDue.getTime() - now.getTime();
    const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));
    const minsLeft = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));

    if (timeLeft < 0) {
        const hoursOver = Math.abs(hoursLeft);
        return `Vencido hace ${hoursOver}h`;
    }

    if (hoursLeft < 1) return `${minsLeft} min`;
    if (hoursLeft < 24) return `${hoursLeft}h ${minsLeft}m`;

    const daysLeft = Math.floor(hoursLeft / 24);
    return `${daysLeft}d ${hoursLeft % 24}h`;
};

// Generate unique ID
export const generateId = (prefix: string = 'ID'): string => {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Validation
export const isValidEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
};

export const isValidPhone = (phone: string): boolean => {
    const re = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
    return re.test(phone);
};
